import {useState, useCallback, useEffect} from 'react';
import {message, Modal, Space} from 'antd';
import {arrayMove} from '@dnd-kit/sortable';
import {BoardService} from './boardService';
import {InfoCircleOutlined} from "@ant-design/icons";
import HttpClient from "@/utils/HttpClient";

// Hook for managing board state and operations
export const useBoardDetail = (boardId) => {
    const [board, setBoard] = useState({});
    const [lists, setLists] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [labels, setLabels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchBoardDetails = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            if (!boardId) {
                setLoading(false);
                return {success: false, error: new Error('No board ID provided')};
            }

            const data = await BoardService.getDetail(boardId);
            setBoard(data);
            setLists(data.lists || []);
            setStatuses(data.statuses || []);
            setLabels(data.labels || []);
            return {success: true};
        } catch (err) {
            setError(err);
            message.error('Failed to fetch board details');
            return {success: false, error: err};
        } finally {
            setLoading(false);
        }
    }, [boardId]);

    // Add useEffect to fetch data when boardId changes
    useEffect(() => {
        fetchBoardDetails();
    }, [boardId, fetchBoardDetails]);

    const updateBoard = async (data) => {
        try {
            const updatedBoard = await BoardService.update(boardId, data);
            setBoard(updatedBoard);
            return {success: true, data: updatedBoard};
        } catch (err) {
            message.error('Failed to update board');
            return {success: false, error: err};
        }
    };

    const deleteBoard = async () => {
        try {
            await BoardService.delete(boardId);
            return {success: true};
        } catch (err) {
            message.error('Failed to delete board');
            return {success: false, error: err};
        }
    };

    return {
        board,
        lists,
        statuses,
        labels,
        loading,
        error,
        fetchBoardDetails,
        updateBoard,
        deleteBoard,
        setLists,
        setStatuses,
        setLabels
    };
};

// Hook for managing list operations
export const useBoardLists = (boardId, lists, setLists) => {
    const addList = async (name) => {
        try {
            const newList = await BoardService.createList(boardId, {
                ...name,
                position: lists.length
            });
            message.success('List added successfully');
            return {success: true, data: newList};
        } catch (err) {
            message.error('Failed to add list');
            return {success: false, error: err};
        }
    };

    const updateList = async (listId, updates) => {
        try {
            const updatedList = await BoardService.updateList(boardId, listId, updates);
            message.success('List updated successfully');
            return {success: true, data: updatedList};
        } catch (err) {
            message.error('Failed to update list');
            return {success: false, error: err};
        }
    };

    const deleteList = async (listId) => {
        try {
            await BoardService.deleteList(boardId, listId);
            setLists(lists.filter(list => list.id !== listId));
            message.success('List deleted successfully');
            return {success: true};
        } catch (err) {
            message.error('Failed to delete list');
            return {success: false, error: err};
        }
    };

    const reorderLists = async (newLists) => {
        try {
            await BoardService.reorderLists(boardId, newLists.map(list => list.id));
            setLists(newLists);
            return {success: true};
        } catch (err) {
            message.error('Failed to reorder lists');
            return {success: false, error: err};
        }
    };

    return {addList, updateList, deleteList, reorderLists};
};

// Hook for managing task operations
export const useBoardTasks = (boardId, lists, setLists) => {
    const addTask = async (data) => {
        try {
            const newTask = await BoardService.createTask(boardId, data);
            const newLists = lists.map(list => {
                if (list.id === data.list_id) {
                    return {
                        ...list,
                        tasks: [...list.tasks, newTask]
                    };
                }
                return list;
            });
            setLists(newLists);
            message.success('Task added successfully');
            return {success: true, data: newTask};
        } catch (err) {
            message.error('Failed to add task');
            return {success: false, error: err};
        }
    };

    const updateTask = async (taskId, updates) => {
        try {
            const updatedTask = await BoardService.updateTask(boardId, taskId, updates);
            message.success('Task updated successfully');
            return {success: true, data: updatedTask};
        } catch (err) {
            message.error('Failed to update task');
            return {success: false, error: err};
        }
    };

    const deleteTask = async (taskId, listId) => {
        try {
            await BoardService.deleteTask(boardId, taskId);
            const updatedLists = lists.map(list => {
                if (list.id === listId) {
                    return {
                        ...list,
                        tasks: list.tasks.filter(task => task.id !== taskId)
                    };
                }
                return list;
            });
            setLists(updatedLists);
            message.success('Task deleted successfully');
            return {success: true};
        } catch (err) {
            message.error('Failed to delete task');
            return {success: false, error: err};
        }
    };

    const moveTask = async (taskId, sourceListId, destinationListId, options) => {
        try {
            if (!destinationListId) {
                throw new Error('Missing required data for moving task');
            }

            const sourceList = lists.find(list => list.id === sourceListId);
            const destinationList = lists.find(list => list.id === destinationListId);

            // Make the API call with the enhanced payload
            await BoardService.moveTask(boardId, taskId, {
                list_id: destinationListId,
                createAnnotation: options.createAnnotation,
                sourceListName: sourceList?.name,
                destinationListName: destinationList?.name,
                position: options.position
            });

            return {
                success: true,
                data: {
                    sourceList,
                    destinationList
                }
            };
        } catch (err) {
            console.error('Move task error:', err);
            message.error('Failed to move task');
            return {success: false, error: err};
        }
    };

    return {addTask, updateTask, deleteTask, moveTask};
};

// Hook for managing the drag and drop state
export const useBoardDragAndDrop = (lists, setLists, moveTask, reorderLists) => {
    const [activeId, setActiveId] = useState(null);
    const [activeItem, setActiveItem] = useState(null);

    // Helper function to find list and task
    const findListAndTask = (taskId) => {
        for (const list of lists) {
            const task = list.tasks.find(t => t.id === taskId);
            if (task) {
                return {list, task};
            }
        }
        return {list: null, task: null};
    };

    const handleDragStart = (event) => {
        const {active} = event;
        setActiveId(active.id);

        if (active.id.toString().startsWith('task-')) {
            const taskId = parseInt(active.id.toString().replace('task-', ''));
            const {task} = findListAndTask(taskId);
            setActiveItem(task);
        } else if (active.id.toString().startsWith('list-')) {
            const listId = parseInt(active.id.toString().replace('list-', ''));
            const list = lists.find(l => l.id === listId);
            setActiveItem(list);
        }
    };

    const handleDragMove = (event) => {
        return;
    };

    const handleDragEnd = async (event) => {
        const {active, over} = event;

        if (!over || active.id === over.id) {
            setActiveId(null);
            setActiveItem(null);
            return;
        }

        try {
            if (active.id.toString().startsWith('list-')) {
                // Handle list reordering
                const oldIndex = lists.findIndex(
                    list => `list-${list.id}` === active.id
                );
                const newIndex = lists.findIndex(
                    list => `list-${list.id}` === over.id
                );

                if (oldIndex !== -1 && newIndex !== -1) {
                    const newLists = [...lists];
                    const [movedList] = newLists.splice(oldIndex, 1);
                    newLists.splice(newIndex, 0, movedList);

                    const updatedLists = newLists.map((list, index) => ({
                        ...list,
                        position: index
                    }));

                    setLists(updatedLists);

                    const result = await reorderLists(updatedLists);
                    if (!result.success) {
                        setLists(lists);
                        message.error('Failed to reorder lists');
                    }
                }
            } else if (active.id.toString().startsWith('task-')) {
                const taskId = parseInt(active.id.toString().replace('task-', ''));
                const {list: sourceList} = findListAndTask(taskId);

                if (!sourceList) return;

                const destinationListId = over.id.toString().startsWith('list-')
                    ? parseInt(over.id.toString().replace('list-', ''))
                    : parseInt(over.data.current?.sortable?.containerId?.replace('list-', '') || 0);

                if (!destinationListId) return;

                const sourceIndex = sourceList.tasks.findIndex(t => t.id === taskId);
                let targetIndex;

                const destinationList = lists.find(l => l.id === destinationListId);
                const isMovingToSameList = sourceList.id === destinationListId;

                // Calculate correct target index
                if (over.id.toString().startsWith('list-')) {
                    // If dropping on list, move to end
                    targetIndex = destinationList.tasks.length;
                } else {
                    // If dropping on a task
                    const overTaskId = parseInt(over.id.toString().replace('task-', ''));
                    const overTaskIndex = destinationList.tasks.findIndex(t => t.id === overTaskId);

                    if (isMovingToSameList && sourceIndex < overTaskIndex) {
                        // When moving down in the same list
                        targetIndex = overTaskIndex;
                    } else {
                        // When moving up or to a different list
                        targetIndex = overTaskIndex;  // Changed from overTaskIndex + 1
                    }
                }

                // Create optimistic update for UI
                const newLists = lists.map(list => {
                    // Remove from source list
                    if (list.id === sourceList.id) {
                        if (isMovingToSameList) {
                            // Reorder within same list
                            const newTasks = [...list.tasks];
                            const [movedTask] = newTasks.splice(sourceIndex, 1);
                            newTasks.splice(targetIndex, 0, movedTask);
                            return {
                                ...list,
                                tasks: newTasks
                            };
                        }
                        return {
                            ...list,
                            tasks: list.tasks.filter(t => t.id !== taskId)
                        };
                    }
                    // Add to destination list
                    if (list.id === destinationListId && !isMovingToSameList) {
                        const newTasks = [...list.tasks];
                        const taskToMove = sourceList.tasks.find(t => t.id === taskId);
                        newTasks.splice(targetIndex, 0, taskToMove);
                        return {
                            ...list,
                            tasks: newTasks
                        };
                    }
                    return list;
                });

                setLists(newLists);

                await handleMoveWithChoice(false);

                async function handleMoveWithChoice(createAnnotationFlag) {
                    const result = await moveTask(taskId, sourceList.id, destinationListId, {
                        sourceListName: sourceList.name,
                        destinationListName: destinationList.name,
                        position: targetIndex,
                        createAnnotation: createAnnotationFlag
                    });

                    if (!result.success) {
                        setLists(lists); // Revert on failure
                        message.error('Failed to move task');
                    }
                }
            }
        } catch (error) {
            console.error('Drag and drop error:', error);
            setLists(lists);
            message.error('Operation failed');
        } finally {
            setActiveId(null);
            setActiveItem(null);
        }
    };

    return {
        activeId,
        activeItem,
        handleDragStart,
        handleDragMove,
        handleDragEnd
    };
};


export const createAnnotation = async (data) => {
    const {
        category = 'Task Movement',
        event_name,
        description,
        selectedProperties = [""]
    } = data;

    const fd = new FormData();

    fd.append('category', category);
    fd.append('event_name', event_name);
    fd.append('description', `${description}`);
    fd.append('show_at', moment().format('YYYY-MM-DD'));

    selectedProperties.forEach((propertyId) => {
        fd.append('google_analytics_property_id[]', propertyId);
    });

    try {
        const response = await HttpClient.post('/annotation', fd);
        return {success: true, data: response.data};
    } catch (err) {
        if (err.response?.status === 402) {
            throw new Error('upgrade_required');
        }
        throw err;
    }
};
